import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Paper,
  Autocomplete,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { ProjectFilter, ProjectStatus } from "../types";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { clientApi } from "../../../pages/masterData/client/apiService";
import { Client } from "../../../pages/masterData/client/types";
import { PROJECT_TERMINOLOGY } from "../enums";

interface ModuleFiltersProps {
  filters: ProjectFilter;
  onFiltersChange: (filters: ProjectFilter) => void;
}

const ModuleFilters: React.FC<ModuleFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [filterKey, setFilterKey] = useState(0);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<ProjectStatus[]>) => {
    const value = event.target.value as unknown as ProjectStatus[];
    onFiltersChange({
      ...filters,
      status: value as unknown as ProjectStatus[] | ProjectStatus.STARTED,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
    setSelectedClient(null);
    setFilterKey((prev) => prev + 1);
  };

  const handleClientFocus = async () => {
    if (!clientsLoaded) {
      try {
        const clients = await clientApi.getClients();
        setClients(clients);
        setClientsLoaded(true);
      } catch (error) {
        console.error("Failed to fetch clients:", error);
      }
    }
  };

  const statusOptions = [
    { value: ProjectStatus.STARTED, label: "Started" },
    { value: ProjectStatus.ON_HOLD, label: "On Hold" },
    { value: ProjectStatus.IN_PROGRESS, label: "In Progress" },
    { value: ProjectStatus.COMPLETED, label: "Completed" },
    { value: ProjectStatus.TERMINATED, label: "Terminated" },
    { value: ProjectStatus.UPCOMING, label: "Upcoming" },
  ];

  // Initialize selected client from filters
  useEffect(() => {
    if (filters.clientId && clients.length > 0) {
      const client = clients.find((c) => c.id === filters.clientId);
      setSelectedClient(client || null);
    } else if (!filters.clientId) {
      setSelectedClient(null);
    }
  }, [filters.clientId, clients]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        mt: 2,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        {/* Search */}
        <TextField
          {...commonTextFieldProps}
          placeholder={`Search ${PROJECT_TERMINOLOGY.PLURAL}...`}
          value={filters.search || ""}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ mr: 1, color: "text.secondary", fontSize: "1rem" }}
              />
            ),
          }}
          // sx={{ minWidth: 200 }}
        />

        {/* Status Filter */}
        <FormControl {...commonTextFieldProps} sx={{ minWidth: 150 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Status</InputLabel>
          <Select
            multiple
            value={(filters.status as unknown as ProjectStatus[]) || []}
            onChange={handleStatusChange}
            input={<OutlinedInput label="Status" {...commonTextFieldProps} />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={
                      statusOptions.find((opt) => opt.value === value)?.label
                    }
                    size="small"
                    sx={{ fontSize: "0.7rem" }}
                  />
                ))}
              </Box>
            )}
            {...commonTextFieldProps.SelectProps}
          >
            {statusOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: "0.75rem" }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Client Filter */}
        <Autocomplete
          key={filterKey}
          options={clients}
          value={selectedClient}
          getOptionLabel={(option) => (option as Client).clientName}
          renderOption={(props, option) => {
            return (
              <li {...props} style={{ fontSize: "0.75rem" }}>
                {(option as Client).clientName}
              </li>
            );
          }}
          noOptionsText="No options found"
          {...commonTextFieldProps}
          sx={{ minWidth: 150 }}
          renderInput={(params) => (
            <TextField {...params} label="Client" {...commonTextFieldProps} />
          )}
          onFocus={handleClientFocus}
          onChange={(event, value) => {
            const client = value as Client;
            setSelectedClient(client);
            onFiltersChange({
              ...filters,
              clientId: client?.id || "",
            });
          }}
          loading={!clientsLoaded}
          loadingText="Loading clients..."
        />
      </Box>
      {/* TO DO : Add more filters like date as well as pagination */}
      {/* clear filter button */}
      {Object.keys(filters).length > 0 && (
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          color="error"
          {...smallButtonProp}
        >
          Clear Filters
        </Button>
      )}
    </Paper>
  );
};

export default ModuleFilters;
